import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { NavbarDashbord } from './navbarDashbord';
import { ShoppingBag, Eye, CheckCircle, XCircle, X, Package } from 'lucide-react';
import '../styles/admin.css';
import { deleteCommande, updateCommande, getDataCommandes } from './action';

export function PageCommandes() {
    const dispatch = useDispatch();
    const location = useLocation();
    const [selectedOrder, setSelectedOrder] = useState(null);

    const commandes = useSelector(state => state.commandes) || [];

    useEffect(() => {
        if (commandes.length === 0) {
            dispatch(getDataCommandes());
        }

        // Handle deep-linking status filter from dashboard
        const params = new URLSearchParams(location.search);
        const statusParam = params.get('status');
        if (statusParam) {
            setStatusFilter(statusParam);
        }
    }, [dispatch, commandes.length, location.search]);

    // Filtering States
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('Tous les statuts');

    // Dynamic Statuses from Data
    const uniqueStatuses = [...new Set(commandes.map(cmd => cmd.statut?.toLowerCase()))].filter(Boolean);

    // Helper to parse date strings (handles YYYY-MM-DD and DD/MM/YYYY)
    const parseDateStr = (dateStr) => {
        if (!dateStr) return new Date(0);

        // If already ISO format YYYY-MM-DD
        if (dateStr.includes('-')) {
            const date = new Date(dateStr);
            // Ensure time is set to 0 to avoid timezone issues during comparison
            date.setHours(0, 0, 0, 0);
            return date;
        }

        // If legacy format DD/MM/YYYY
        if (dateStr.includes('/')) {
            const parts = dateStr.split('/');
            if (parts.length === 3) {
                const [day, month, year] = parts;
                return new Date(`${year}-${month}-${day}`);
            }
        }

        return new Date(dateStr);
    };

    // Filtering Logic
    const filteredCommandes = commandes.filter(cmd => {
        const cmdDate = parseDateStr(cmd.date);

        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            if (cmdDate < start) return false;
        }

        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (cmdDate > end) return false;
        }

        if (statusFilter !== 'Tous les statuts' && cmd.statut?.toLowerCase() !== statusFilter.toLowerCase()) return false;

        return true;
    });

    // CHANGEMENT DE STATUT : de "en attente" -> "en cours" -> "validé"
    const handleStatusUpdate = (commande) => {
        // Nettoyage et normalisation du statut
        const current = String(commande.statut || "").toLowerCase().trim();
        let nextStatus = "";

        // Définition du pipeline de changement
        if (current === "en attente" || current === "en attende") {
            nextStatus = "en cours";
        } else if (current === "en cours") {
            nextStatus = "livrée";
        } else {
            // Déjà livré ou autre statut, on ne fait rien
            return;
        }

        // Dispatch de la mise à jour
        dispatch(updateCommande({ ...commande, statut: nextStatus }));
    };

    // SUPPRESSION DE COMMANDE
    const handleSupprimer = (id) => {
        if (window.confirm("Action irréversible : souhaitez-vous supprimer cette commande ?")) {
            dispatch(deleteCommande(id));
        }
    };

    return (
        <div className="admin-layout">
            <NavbarDashbord />
            <main className="admin-main-content">
                <header className="admin-header">
                    <div className="admin-title-group">
                        <h1>Gestion des Commandes</h1>
                        <p>Suivez et validez les achats de vos clients en temps réel ({filteredCommandes.length} total).</p>
                    </div>
                </header>

                <div className="admin-table-container">
                    <div className="table-header-actions" style={{ flexWrap: 'wrap', gap: '15px' }}>
                        <div className="admin-filters" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div className="date-range-group" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <span style={{ fontSize: '0.7rem', color: 'var(--admin-text-muted)' }}>De:</span>
                                <input type="date" className="admin-select" value={startDate} onChange={e => setStartDate(e.target.value)} />
                                <span style={{ fontSize: '0.7rem', color: 'var(--admin-text-muted)' }}>À:</span>
                                <input type="date" className="admin-select" value={endDate} onChange={e => setEndDate(e.target.value)} />
                                {(startDate || endDate) && <button onClick={() => { setStartDate(''); setEndDate(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-danger)' }}><X size={14} /></button>}
                            </div>
                            <select className="admin-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                                <option value="Tous les statuts">Tous les statuts</option>
                                {uniqueStatuses.map((st, i) => (
                                    <option key={i} value={st} style={{ textTransform: 'capitalize' }}>
                                        {st.charAt(0).toUpperCase() + st.slice(1)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <table className="account-table order-table">
                        <thead>
                            <tr>
                                <th>N° Commande</th>
                                <th>Client</th>
                                <th>Montant Total</th>
                                <th>Date</th>
                                <th>Statut</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCommandes.map((cmd) => (
                                <tr key={cmd.id}>
                                    <td style={{ fontWeight: '800', color: 'var(--admin-primary)' }}>#{cmd.id}</td>
                                    <td style={{ fontWeight: '700' }}>{cmd.username_Client}</td>
                                    <td style={{ fontWeight: '800', color: 'var(--admin-secondary)' }}>{cmd.total} DH</td>
                                    <td style={{ fontSize: '0.85rem' }}>{cmd.date}</td>
                                    <td>
                                        <span className={`order-badge ${cmd.statut?.toLowerCase() === 'livrée' ? 'badge-success' :
                                            cmd.statut?.toLowerCase() === 'en cours' ? 'badge-warning' : 'badge-danger'
                                            }`}>
                                            {cmd.statut}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="order-action-btns">
                                            {/* VOIR DETAILS */}
                                            <button 
                                                className="order-btn-icon" 
                                                onClick={() => setSelectedOrder(cmd)} 
                                                title="Voir Détails"
                                            >
                                                <Eye size={16} />
                                            </button>
 
                                            {/* MARQUER COMME EN COURS / VALIDER */}
                                            <button
                                                className="order-btn-icon order-btn-modify"
                                                onClick={() => handleStatusUpdate(cmd)}
                                                disabled={cmd.statut?.toLowerCase() === "livrée"}
                                                title={cmd.statut?.toLowerCase() === "livrée" ? "Commande Livrée" : "Passer au statut suivant"}
                                                style={{ 
                                                    opacity: cmd.statut?.toLowerCase() === "livrée" ? 0.35 : 1,
                                                    cursor: cmd.statut?.toLowerCase() === "livrée" ? "not-allowed" : "pointer" 
                                                }}
                                            >
                                                <CheckCircle size={16} />
                                            </button>
 
                                            {/* SUPPRIMER LA COMMANDE */}
                                            <button
                                                className="order-btn-icon order-btn-delete"
                                                onClick={() => handleSupprimer(cmd.id)}
                                                title="Supprimer la commande"
                                            >
                                                <XCircle size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filteredCommandes.length === 0 && (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: 'var(--admin-text-muted)' }}>Aucune commande correspondante.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Detail Modal */}
                {selectedOrder && (
                    <div className="admin-modal-overlay" onClick={() => setSelectedOrder(null)}>
                        <div className="admin-modal-content" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Package /> Détails Commande #{selectedOrder.id}</h2>
                                <button className="modal-close-btn" onClick={() => setSelectedOrder(null)}>&times;</button>
                            </div>
                            <div className="modal-body" style={{ minWidth: '450px' }}>
                                <div style={{ background: 'var(--admin-bg)', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '1px solid var(--admin-border)' }}>
                                    <p style={{ margin: '4px 0' }}><strong>Client:</strong> <span>{selectedOrder.username_Client}</span></p>
                                    <p style={{ margin: '4px 0' }}><strong>Date de commande:</strong> <span>{selectedOrder.date}</span></p>
                                    <p style={{ margin: '4px 0' }}><strong>Statut:</strong> <span className={`admin-badge ${selectedOrder.statut?.toLowerCase() === 'livrée' ? 'badge-success' : selectedOrder.statut?.toLowerCase() === 'en cours' ? 'badge-warning' : 'badge-danger'}`}>{selectedOrder.statut}</span></p>
                                </div>

                                                    <div className="order-products-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <h3 style={{ fontSize: '0.9rem', color: 'var(--admin-text-muted)', textTransform: 'uppercase', marginBottom: '5px' }}>Produits achetés</h3>
                                    {selectedOrder.produits?.map((p, idx) => (
                                        <div key={idx} className="order-product-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', background: 'white', borderRadius: '10px', border: '1px solid var(--admin-border)', boxShadow: 'var(--admin-shadow-sm)' }}>
                                            <div className="order-product-info" style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span className="order-product-id" style={{ fontWeight: '800', fontSize: '0.95rem' }}>ID Produit: #{p.id}</span>
                                                <span className="order-product-qty" style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>Qté: {p.quantite} × {p.prix} DH</span>
                                            </div>
                                            <div className="order-product-amount" style={{ textAlign: 'right' }}>
                                                <span className="order-product-price" style={{ fontWeight: '700', color: 'var(--admin-secondary)' }}>{p.quantite * p.prix} DH</span>
                                            </div>
                                        </div>
                                    ))}
 
                                <div className="order-total-summary" style={{ marginTop: '25px', paddingTop: '15px', borderTop: '2px solid var(--admin-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span className="order-total-label" style={{ fontSize: '1rem', fontWeight: '700' }}>TOTAL COMMANDE</span>
                                    <span className="order-total-price" style={{ fontSize: '1.4rem', fontWeight: '900', color: 'var(--admin-secondary)' }}>{selectedOrder.total} DH</span>
                                </div>
                </div>
                            </div>
                            <div className="modal-footer" style={{ padding: '20px', textAlign: 'center' }}>
                                <button className="admin-btn-save" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setSelectedOrder(null)}>Fermer</button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

